// MVP runtime flags — import before mock data / services.
import {
  SA_MOCK_ONLY_PAGES,
  SA_MVP_HIDDEN_ROUTES,
  SA_MVP_LEGACY_ROUTES,
  SA_SHOW_FUTURE_PAGES_KEY,
  getShowFuturePages as getShowFuturePagesPure,
  isFuturePage as isFuturePagePure,
  isLocalHost as isLocalHostPure,
  isMockOnlyPage as isMockOnlyPagePure,
  isPrototypeRoute as isPrototypeRoutePure,
  isRouteAllowedInMvp as isRouteAllowedInMvpPure,
  resolveLegacyRoute as resolveLegacyRoutePure,
  setShowFuturePages as setShowFuturePagesPure,
} from '../src/navigation/mvp-route-rules.js';

const pre = typeof window !== 'undefined' ? window : {};

export const USE_REAL_TENANTS_API = pre.USE_REAL_TENANTS_API !== false;
export const USE_MOCK_TENANTS = pre.USE_MOCK_TENANTS === true;
export const USE_REAL_NOTIFICATIONS_API = pre.USE_REAL_NOTIFICATIONS_API !== false;
export const USE_MOCK_NOTIFICATIONS = pre.USE_MOCK_NOTIFICATIONS === true;
export const USE_REAL_BILLING_API = pre.USE_REAL_BILLING_API !== false;
export const USE_MOCK_BILLING = pre.USE_MOCK_BILLING === true;
export const USE_REAL_REPAIRS_API = pre.USE_REAL_REPAIRS_API === true;
export const USE_MOCK_REPAIRS = pre.USE_MOCK_REPAIRS === true;

export const FEATURE_SUPPORT_MODULES = pre.FEATURE_SUPPORT_MODULES === true;
export const FEATURE_REPAIRS_MODULE = pre.FEATURE_REPAIRS_MODULE === true;
export const FEATURE_OBSERVABILITY = pre.FEATURE_OBSERVABILITY === true;
export const FEATURE_INTEGRATIONS = pre.FEATURE_INTEGRATIONS === true;
export const FEATURE_IMPERSONATION = pre.FEATURE_IMPERSONATION === true;

export { SA_MVP_LEGACY_ROUTES, SA_MVP_HIDDEN_ROUTES, SA_MOCK_ONLY_PAGES, SA_SHOW_FUTURE_PAGES_KEY };

export function saShouldUseMockTenants() {
  if (USE_MOCK_TENANTS === true) return true;
  if (USE_REAL_TENANTS_API === false) return true;
  return false;
}

export function saIsRealTenantsMode() {
  return !saShouldUseMockTenants();
}

export function saShouldUseMockNotifications() {
  if (USE_MOCK_NOTIFICATIONS === true) return true;
  if (USE_REAL_NOTIFICATIONS_API === false) return true;
  return false;
}

export function saIsRealNotificationsMode() {
  return !saShouldUseMockNotifications();
}

export function saShouldUseMockBilling() {
  if (USE_MOCK_BILLING === true) return true;
  if (USE_REAL_BILLING_API === false) return true;
  return false;
}

export function saIsRealBillingMode() {
  return !saShouldUseMockBilling();
}

export function saShouldUseMockRepairs() {
  if (USE_MOCK_REPAIRS === true) return true;
  if (USE_REAL_REPAIRS_API !== true) return true;
  return false;
}

export function saIsRealRepairsMode() {
  return !saShouldUseMockRepairs();
}

export function saResolveLegacyRoute(page) {
  return resolveLegacyRoutePure(page, SA_MVP_LEGACY_ROUTES);
}

export function saIsRouteAllowedInMvp(page) {
  return isRouteAllowedInMvpPure(page, {
    legacyRoutes: SA_MVP_LEGACY_ROUTES,
    hiddenRoutes: SA_MVP_HIDDEN_ROUTES,
    featureIntegrations: FEATURE_INTEGRATIONS === true,
    featureObservability: FEATURE_OBSERVABILITY === true,
  });
}

export function saIsPrototypeRoute(page) {
  return isPrototypeRoutePure(page, {
    featureIntegrations: FEATURE_INTEGRATIONS === true,
    featureObservability: FEATURE_OBSERVABILITY === true,
    featureRepairs: FEATURE_REPAIRS_MODULE === true,
  });
}

export function saIsLocalHost(hostname) {
  const host = String(
    hostname != null
      ? hostname
      : (typeof window !== 'undefined' ? window.location.hostname : '')
  );
  return isLocalHostPure(host);
}

export function saIsFuturePage(page) {
  return isFuturePagePure(page, {
    legacyRoutes: SA_MVP_LEGACY_ROUTES,
    featureIntegrations: FEATURE_INTEGRATIONS === true,
    featureObservability: FEATURE_OBSERVABILITY === true,
    featureRepairs: FEATURE_REPAIRS_MODULE === true,
  });
}

export function saGetShowFuturePages() {
  return getShowFuturePagesPure({
    getItem: (key) => {
      if (typeof localStorage === 'undefined') return null;
      return localStorage.getItem(key);
    },
    hostname: typeof window !== 'undefined' ? window.location.hostname : '',
    storageKey: SA_SHOW_FUTURE_PAGES_KEY,
  });
}

export function saSetShowFuturePages(value) {
  return setShowFuturePagesPure(value, {
    setItem: (key, next) => {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(key, next);
    },
    storageKey: SA_SHOW_FUTURE_PAGES_KEY,
  });
}

/** Pages that are entirely toast/local mock — hidden in operational UI until a real API exists. */
export function saIsMockOnlyPage(page) {
  return isMockOnlyPagePure(page, {
    legacyRoutes: SA_MVP_LEGACY_ROUTES,
    mockOnlyPages: SA_MOCK_ONLY_PAGES,
  });
}
